import { z } from 'zod';
import { validateEmailDomain } from './email-validation';

// Enhanced email validation with domain checking
export const emailSchema = z.string()
  .email('Please enter a valid email address')
  .max(254, 'Email address is too long')
  .refine((email) => {
    const validation = validateEmailDomain(email);
    return validation.isValid;
  }, (email) => {
    const validation = validateEmailDomain(email);
    return { message: validation.reason || 'Invalid email domain' };
  });

// Input validation schemas
export const passwordSchema = z.string().min(8).max(128);
export const titleSchema = z.string().min(1).max(500);
export const abstractSchema = z.string().min(10).max(5000);
export const commentSchema = z.string().min(1).max(2000);
export const urlSchema = z.string().url().max(2048);

// File validation
export const validateFileUpload = (file: File): { isValid: boolean; error?: string } => {
  // Check file size (max 50MB)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    return { isValid: false, error: 'File size exceeds 50MB limit' };
  }
  
  // Check file type
  const allowedTypes = ['application/pdf'];
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'Only PDF files are allowed' };
  }
  
  // Check file name
  const allowedExtensions = ['.pdf'];
  const hasValidExtension = allowedExtensions.some(ext => 
    file.name.toLowerCase().endsWith(ext)
  );
  
  if (!hasValidExtension) {
    return { isValid: false, error: 'Invalid file extension' };
  }
  
  return { isValid: true };
};

// Rate limiting helper
export const createRateLimit = (windowMs: number, maxRequests: number) => {
  const requests = new Map<string, { count: number; resetTime: number }>();
  
  return (identifier: string): boolean => {
    const now = Date.now();
    const userRequests = requests.get(identifier);
    
    if (!userRequests || now > userRequests.resetTime) {
      requests.set(identifier, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (userRequests.count >= maxRequests) {
      return false;
    }
    
    userRequests.count++;
    return true;
  };
};
